import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  TablePagination,
  Avatar,
  Tooltip,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Check as CheckIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Comment as CommentIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { TaskDTO } from '../api/generated/requests/types.gen';
import { MinIoFileStorageService } from '../api/generated/requests/services.gen';
import { useUserServiceGetApiUserMe, useTasksServiceGetApiTasksStatsComprehensive } from '../api/generated/queries/queries';
import { useUserData } from '../hooks/useUserData';
import { useTasksServiceGetApiTasksPaginated } from '../api/generated/queries/queries';
import { useTaskMutations } from '../hooks/useTaskData';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import * as Yup from 'yup';
import NewTaskButton from '../components/Tasks/NewTaskButton';
import TaskDetailDialog from '../components/Tasks/TaskDetailDialog';
import TaskDialog from '../components/Tasks/TaskDialog';
import LeadDetailDialog from '../components/Leads/LeadDetailDialog';
import TaskSearchBar from '../components/Tasks/TaskSearchBar';
import UserSearchMenu from '../components/Common/UserSearchMenu';
import ColumnConfigDialog, { ColumnConfig } from '../components/Common/ColumnConfigDialog';
import TabConfigDialog, { TabConfig } from '../components/Common/TabConfigDialog';
import {
  getPriorityColor,
  getPriorityLabel,
  getStatusColor, getStatusLabel,
  getTaskTypeLabel,
  getTaskTypeColor,
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
  TASK_TYPE
} from '../models/TaskDTO';
import { useSearchParams } from 'react-router-dom';
// import { AvatarCacheService } from '../services/avatarCacheService';

type TaskColumn = {
  id: keyof TaskDTO | 'actions';
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
};

const defaultColumns: TaskColumn[] = [
  { id: 'id', label: 'Id', minWidth: 80 },
  { id: 'title', label: 'Tytuł', minWidth: 120 },
  { id: 'status', label: 'Status' },
  { id: 'priority', label: 'Priorytet' },
  { id: 'taskType', label: 'Typ zadania' },
  { id: 'assignedToUserId', label: 'Przypisany do' },
  { id: 'leads', label: 'Kontakty' },
  { id: 'startDate', label: 'Termin od' },
  { id: 'endDate', label: 'Termin do' },
  { id: 'createdAt', label: 'Data utworzenia' },
  { id: 'actions', label: 'Akcje', align: 'right' }
];

const defaultTabs: TabConfig[] = [
  { id: '0', label: 'Wszystkie', visible: true, isDefault: true, view: 'ALL' },
  { id: '1', label: 'Do zrobienia', visible: true, isDefault: false, view: 'TODO' },
  { id: '2', label: 'W trakcie', visible: true, isDefault: false, view: 'IN_PROGRESS' },
  { id: '3', label: 'Zakończone', visible: true, isDefault: false, view: 'COMPLETED' },
  { id: '4', label: 'Przeterminowane', visible: true, isDefault: false, view: 'OVERDUE' },
  { id: '5', label: 'Moje zadania', visible: true, isDefault: false, view: 'ASSIGNED_TO_ME' }
];
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`task-tabpanel-${index}`}
      aria-labelledby={`task-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `task-tab-${index}`,
    'aria-controls': `task-tabpanel-${index}`,
  };
}
Yup.object({
  title: Yup.string().required('Tytuł jest wymagany'),
  description: Yup.string(),
  status: Yup.string().required('Status jest wymagany'),
  priority: Yup.string().oneOf(['A', 'B', 'C', 'D', 'E']).required('Priorytet jest wymagany'),
  taskType: Yup.string().required('Typ zadania jest wymagany'),
  startDate: Yup.date()
      .required('Termin od jest wymagany'),
  endDate: Yup.date()
      .required('Termin do jest wymagany')
      .min(Yup.ref('startDate'), 'Termin do nie może być wcześniejszy niż termin od'),
});
const TasksPage: React.FC = () => {
  // Initialize tabs with saved configuration to prevent flickering
  const getInitialTabConfigs = (): TabConfig[] => {
    const savedConfig = localStorage.getItem('tasks-tabs-config');
    if (savedConfig) {
      try {
        const parsedConfig: TabConfig[] = JSON.parse(savedConfig);
        if (Array.isArray(parsedConfig)) {
          return parsedConfig;
        }
      } catch (error) {
        console.error('Błąd podczas parsowania zapisanych zakładek:', error);
      }
    }
    return [...defaultTabs];
  };

  // Initialize current view and tab value based on saved configuration
  const getInitialViewAndTab = () => {
    const initialTabs = getInitialTabConfigs();
    const defaultTab = initialTabs.find(tab => tab.isDefault && tab.visible);
    if (defaultTab) {
      const visibleTabs = initialTabs.filter(tab => tab.visible);
      const defaultTabIndex = visibleTabs.findIndex(tab => tab.id === defaultTab.id);
      return {
        view: defaultTab.view,
        tabValue: defaultTabIndex !== -1 ? defaultTabIndex : 0
      };
    }
    return { view: 'ALL' as const, tabValue: 0 };
  };

  const initialState = getInitialViewAndTab();
  const [tabValue, setTabValue] = useState(initialState.tabValue);
  const [editTask, setEditTask] = useState<TaskDTO | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<TaskDTO | null>(null);
  const [taskFiles, setTaskFiles] = useState<any[]>([]);
  const [priorityMenuAnchor, setPriorityMenuAnchor] = useState<{ element: HTMLElement | null, taskId: number }>({ element: null, taskId: 0 });
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<{ element: HTMLElement | null, taskId: number }>({ element: null, taskId: 0 });
  const [taskTypeMenuAnchor, setTaskTypeMenuAnchor] = useState<{ element: HTMLElement | null, taskId: number }>({ element: null, taskId: 0 });
  const [assignUserMenuAnchor, setAssignUserMenuAnchor] = useState<{ element: HTMLElement | null, taskId: number }>({ element: null, taskId: 0 });
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [openLeadDetailDialog, setOpenLeadDetailDialog] = useState(false);
  const [, setUserSearchTerm] = useState('');
  const [userFilterAnchorEl, setUserFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [tabConfigOpen, setTabConfigOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Filter state variables
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [taskTypeFilter, setTaskTypeFilter] = useState<string>('');
  const [userFilter, setUserFilter] = useState<string>('');
  const [clientFilter, setClientFilter] = useState<number | undefined>(undefined);
  const [startDateFromFilter, setStartDateFromFilter] = useState<Date | null>(null);
  const [startDateToFilter, setStartDateToFilter] = useState<Date | null>(null);
  const [createdAtFromFilter, setCreatedAtFromFilter] = useState<Date | null>(null);
  const [createdAtToFilter, setCreatedAtToFilter] = useState<Date | null>(null);
  const [columns, setColumns] = useState<TaskColumn[]>(defaultColumns);
  const [tabs, setTabs] = useState<TabConfig[]>(getInitialTabConfigs());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Initialize current view based on saved configuration
  const getInitialView = (): 'ALL' | 'TODO' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE' | 'ASSIGNED_TO_ME' => {
    const initialTabs = getInitialTabConfigs();
    const defaultTab = initialTabs.find(tab => tab.isDefault && tab.visible);
    return defaultTab ? defaultTab.view : 'ALL';
  };

  const [currentView, setCurrentView] = useState<'ALL' | 'TODO' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE' | 'ASSIGNED_TO_ME'>(getInitialView());
  const [sortBy, setSortBy] = useState<'ID' | 'TITLE' | 'STATUS' | 'PRIORITY' | 'TASK_TYPE' | 'ASSIGNED_TO' | 'DEADLINE' | 'CREATED_AT'>('ID');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  
  // Avatar cache state
  // const [cachedAvatars, setCachedAvatars] = useState<Map<string, { avatarUrl: string | null; timestamp: number }>>(new Map());

  // Fetch paginated task data
  const { data: taskPagedResponse, isLoading, refetch: refreshTasks } = useTasksServiceGetApiTasksPaginated({
    page,
    size: rowsPerPage,
    titleOrDescription: searchTerm || undefined,
    assignedToUserId: userFilter || undefined,
    clientId: clientFilter,
    taskType: taskTypeFilter ? taskTypeFilter as "EMAIL" | "PHONE" | "MEETING" | "MESSAGE" | "OTHER" : undefined,
    priority: priorityFilter ? priorityFilter as "A" | "B" | "C" | "D" | "E" : undefined,
    deadlineFrom: startDateFromFilter ? startDateFromFilter.toISOString() : undefined,
    deadlineTo: startDateToFilter ? startDateToFilter.toISOString() : undefined,
    createdFrom: createdAtFromFilter ? createdAtFromFilter.toISOString() : undefined,
    createdTo: createdAtToFilter ? createdAtToFilter.toISOString() : undefined,
    view: currentView,
    sortBy,
    sortDirection
  });

  const { deleteTask, assignUser, unassignUser, updateTask } = useTaskMutations();

  // Extract data from paginated response
  const tasks = taskPagedResponse?.content || [];
  const pageInfo = taskPagedResponse?.page;
  const totalElements = pageInfo?.totalElements || 0;
  const totalPages = pageInfo?.totalPages || 0;

  // Fetch user data using optimized hook
  const {domainUsers, isLoading: loadingUsers } = useUserData();
  const { data: currentUser } = useUserServiceGetApiUserMe();
  
  // Fetch comprehensive task statistics
  const { data: taskStats } = useTasksServiceGetApiTasksStatsComprehensive();
  
  // Check if user is admin
  const isAdmin = currentUser?.roles?.includes('api-admin');

  // Function to get task count for a specific tab view
  const getTaskCountForView = (view: 'ALL' | 'TODO' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE' | 'ASSIGNED_TO_ME'): number => {
    if (!taskStats) return 0;
    
    switch (view) {
      case 'ALL':
        return taskStats.totalTasks || 0;
      case 'TODO':
        return taskStats.tasksByStatus?.['TODO'] || 0;
      case 'IN_PROGRESS':
        return taskStats.tasksByStatus?.['IN_PROGRESS'] || 0;
      case 'COMPLETED':
        return taskStats.tasksByStatus?.['DONE'] || 0;
      case 'OVERDUE':
        return taskStats.overdueTasks || 0;
      case 'ASSIGNED_TO_ME':
        return taskStats.currentUserTasks || 0;
      default:
        return 0;
    }
  };

  // Funkcja do przypisywania zadania do użytkownika
  const handleAssignUser = async (userId: string) => {
    const taskId = assignUserMenuAnchor.taskId;
    if (!taskId) return;

    try {
      await assignUser({
        id: taskId,
        keycloakUserId: userId
      });

      setAssignUserMenuAnchor({ element: null, taskId: 0 });
      setUserSearchTerm('');
      
      // Refresh data after assignment change - dispatch global event to refresh all tabs
      window.dispatchEvent(new CustomEvent('refresh-tasks'));
    } catch (error) {
      console.error('Error assigning user:', error);
    }
  };

  // Funkcja do usuwania przypisania zadania
  const handleUnassignUser = async () => {
    const taskId = assignUserMenuAnchor.taskId;
    if (!taskId) return;

    try {
      await unassignUser({ id: taskId });
      setAssignUserMenuAnchor({ element: null, taskId: 0 });
      
      // Refresh data after unassignment - dispatch global event to refresh all tabs
      window.dispatchEvent(new CustomEvent('refresh-tasks'));
    } catch (error) {
      console.error('Error unassigning user:', error);
    }
  };

  // Funkcja pomocnicza do uzyskania inicjałów użytkownika
  const getUserInitials = (userId?: string) => {
    if (!userId) return '';

    const user = domainUsers.find(u => u.id === userId);
    if (!user) return '';

    const firstName = user.firstName || '';
    const lastName = user.lastName || '';

    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Funkcja pomocnicza do uzyskania pełnego imienia i nazwiska użytkownika
  const getUserFullName = (userId?: string) => {
    if (!userId) return 'Nieprzypisane';

    const user = domainUsers.find((u: any) => u.id === userId);
    if (!user) return 'Nieznany użytkownik';

    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.login || 'Nieznany użytkownik';
  };

  // Funkcja pomocnicza do uzyskania koloru użytkownika
  const getUserColor = (userId?: string) => {
    if (!userId) return 'grey.300';

    const user = domainUsers.find((u: any) => u.id === userId);
    if (!user) return 'grey.300';

    return user.color || 'primary.main';
  };



  // Funkcja do kompleksowego odświeżania cache zadań
  const invalidateAllTaskQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasks'] }),
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksMyAssigned'] }),
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksRecent'] }),
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksOverdue'] }),
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksPaginated'] }),
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksMyCreated'] }),
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksMyAssignedOverdue'] }),
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksEndDateBetween'] }),
      queryClient.invalidateQueries({ queryKey: ['TasksServiceGetApiTasksCreatedAtBetween'] })
    ]);
  };

  // Filtrowanie użytkowników na podstawie wyszukiwania
  useEffect(() => {
    // Dodanie globalnego nasłuchiwacza zdarzeń dla odświeżania zadań
    const handleRefreshTasks = async () => {
      // Invalidate all task-related queries to clear cache completely
      await invalidateAllTaskQueries();
    };

    window.addEventListener('refresh-tasks', handleRefreshTasks);

    return () => {
      window.removeEventListener('refresh-tasks', handleRefreshTasks);
    };
  }, [queryClient]);

  // Preload avatars when tasks are loaded
  // useEffect(() => {
  //   if (tasks && tasks.length > 0) {
  //     const userIds = tasks
  //       .map(task => task.assignedToUserId)
  //       .filter((userId): userId is string => Boolean(userId));
      
  //     if (userIds.length > 0) {
  //       AvatarCacheService.preloadAvatars(userIds).then(() => {
  //         // Update local cache state after preloading
  //         const newCachedAvatars = new Map(cachedAvatars);
  //         userIds.forEach(userId => {
  //           const cached = AvatarCacheService.getCachedAvatar(userId);
  //           if (cached) {
  //             newCachedAvatars.set(userId, {
  //               avatarUrl: cached.avatarUrl,
  //               timestamp: cached.timestamp
  //             });
  //           }
  //         });
  //         setCachedAvatars(newCachedAvatars);
  //       });
  //     }
  //   }
  // }, [tasks]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0); // Reset page when switching tabs
    
    // Get visible tabs and map index to view
    const visibleTabs = tabs.filter(tab => tab.visible);
    const selectedTab = visibleTabs[newValue];
    
    if (selectedTab) {
      setCurrentView(selectedTab.view);
    }
  };

  const handleEditClick = (task: TaskDTO) => {
    setEditTask(task);
  };

  const handleDeleteClick = async (task: TaskDTO) => {
    setTaskToDelete(task);

    // Fetch associated files
    if (task.id) {
      try {
        const files = await MinIoFileStorageService.getApiFilesByRelatedObjectByIdByType({
          id: task.id.toString(),
          type: 'TASK'
        });
        setTaskFiles(files || []);
      } catch (error) {
        console.error('Error fetching task files:', error);
        setTaskFiles([]);
      }
    } else {
      setTaskFiles([]);
    }

    setDeleteConfirmOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditTask(null);
  };

  const handleCloseDeleteDialog = () => {
    setTaskToDelete(null);
    setDeleteConfirmOpen(false);
    setTaskFiles([]);
  };

  const handleOpenDetailDialog = (taskId: number, initialTab = 0) => {
    setSelectedTaskId(taskId);
    setSelectedTabIndex(initialTab);
    setDetailDialogOpen(true);
  };

  const handleOpenCommentsDialog = (e: React.MouseEvent, taskId: number) => {
    e.stopPropagation(); // Prevent row click event
    handleOpenDetailDialog(taskId, 1); // 1 is the index of the comments tab
  };

  const handleCloseDetailDialog = () => {
    setSelectedTaskId(null);
    setSelectedTabIndex(0); // Reset tab index to default
    setDetailDialogOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (!taskToDelete?.id) return;

    deleteTask({ id: taskToDelete.id });
    handleCloseDeleteDialog();
  };

  const handleClosePriorityMenu = () => {
    setPriorityMenuAnchor({ element: null, taskId: 0 });
  };

  const handlePriorityChange = async (newPriority: 'A' | 'B' | 'C' | 'D' | 'E') => {
    const taskId = priorityMenuAnchor.taskId;
    if (!taskId) return;

    // Znajdź zadanie do zaktualizowania
    const taskToUpdate = tasks.find(task => task.id === taskId);
    if (!taskToUpdate) return;

    // Zaktualizuj priorytet zadania
    const updatedTask = { ...taskToUpdate, priority: newPriority };

    try {
      await updateTask({
        id: taskId,
        requestBody: updatedTask
      });

      handleClosePriorityMenu();
      
      // Refresh data after priority change - dispatch global event to refresh all tabs
      window.dispatchEvent(new CustomEvent('refresh-tasks'));
    } catch (error) {
      console.error('Error updating priority:', error);
      handleClosePriorityMenu();
    }
  };

  const handleCloseStatusMenu = () => {
    setStatusMenuAnchor({ element: null, taskId: 0 });
  };

  const handleStatusChange = async (newStatus: 'TODO' | 'IN_PROGRESS' | 'CANCELED' | 'DONE') => {
    const taskId = statusMenuAnchor.taskId;
    if (!taskId) return;

    // Znajdź zadanie do zaktualizowania
    const taskToUpdate = tasks.find(task => task.id === taskId);
    if (!taskToUpdate) return;

    // Zaktualizuj status zadania
    const updatedTask = { ...taskToUpdate, status: newStatus };

    try {
      await updateTask({
        id: taskId,
        requestBody: updatedTask
      });

      handleCloseStatusMenu();
      
      // Refresh data after status change - dispatch global event to refresh all tabs
      window.dispatchEvent(new CustomEvent('refresh-tasks'));
    } catch (error) {
      console.error('Error updating status:', error);
      handleCloseStatusMenu();
    }
  };

  const handleCloseTaskTypeMenu = () => {
    setTaskTypeMenuAnchor({ element: null, taskId: 0 });
  };

  const handleTaskTypeChange = async (newTaskType: 'EMAIL' | 'PHONE' | 'MEETING' | 'MESSAGE' | 'OTHER') => {
    const taskId = taskTypeMenuAnchor.taskId;
    if (!taskId) return;

    // Znajdź zadanie do zaktualizowania
    const taskToUpdate = tasks.find(task => task.id === taskId);
    if (!taskToUpdate) return;

    // Zaktualizuj typ zadania
    const updatedTask = { ...taskToUpdate, taskType: newTaskType };

    try {
      await updateTask({
        id: taskId,
        requestBody: updatedTask
      });

      handleCloseTaskTypeMenu();
      
      // Refresh data after task type change - dispatch global event to refresh all tabs
      window.dispatchEvent(new CustomEvent('refresh-tasks'));
    } catch (error) {
      console.error('Error updating task type:', error);
      handleCloseTaskTypeMenu();
    }
  };
// Wrapper function for TaskDialog compatibility
  const handleSaveTask = async (task: TaskDTO) => {
    if (!editTask?.id) return;

    try {
      await updateTask({
        id: editTask.id,
        requestBody: task
      });

      handleCloseEditDialog();
      
      // Refresh data after task save - dispatch global event to refresh all tabs
      window.dispatchEvent(new CustomEvent('refresh-tasks'));
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };


  // Inicjalizacja kolumn z localStorage
  useEffect(() => {
    const savedConfig = localStorage.getItem('tasks-columns-config');
    if (savedConfig) {
      try {
        const parsedConfig: ColumnConfig[] = JSON.parse(savedConfig);
        if (Array.isArray(parsedConfig)) {
          // Convert ColumnConfig back to TaskColumn format and filter visible columns
          const visibleColumns = parsedConfig
            .filter(config => config.visible)
            .map(config => {
              const originalColumn = defaultColumns.find(col => col.id === config.id);
              return originalColumn ? {
                ...originalColumn,
                label: config.label
              } : null;
            })
            .filter(Boolean) as TaskColumn[];
          
          if (visibleColumns.length > 0) {
            setColumns(visibleColumns);
          }
        }
      } catch (error) {
        console.error('Błąd podczas parsowania zapisanych kolumn:', error);
      }
    }
  }, []);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const taskId = searchParams.get('taskId');
    if (taskId) {
      const parsedId = parseInt(taskId, 10);
      if (!isNaN(parsedId)) {
        setSelectedTaskId(parsedId);
        setDetailDialogOpen(true);
      }
    }
  }, [searchParams]);


  // Obsługa konfiguracji kolumn
  const handleColumnConfigSave = (columnConfigs: ColumnConfig[]) => {
    // Save configuration to localStorage
    localStorage.setItem('tasks-columns-config', JSON.stringify(columnConfigs));
    
    // Convert ColumnConfig to TaskColumn format and filter visible columns
    const visibleColumns = columnConfigs
      .filter(config => config.visible)
      .map(config => {
        const originalColumn = defaultColumns.find(col => col.id === config.id);
        return originalColumn ? {
          ...originalColumn,
          label: config.label
        } : null;
      })
      .filter(Boolean) as TaskColumn[];
    
    setColumns(visibleColumns);
    enqueueSnackbar('Konfiguracja kolumn została zapisana', { variant: 'success' });
  };

  const getColumnConfigs = (): ColumnConfig[] => {
    const savedConfig = localStorage.getItem('tasks-columns-config');
    
    // Create default configuration
    const defaultConfig = defaultColumns.map(col => ({
      id: col.id.toString(),
      label: col.label,
      visible: true,
      minWidth: col.minWidth,
      align: col.align
    }));
    
    if (savedConfig) {
      try {
        const parsedConfig: ColumnConfig[] = JSON.parse(savedConfig);
        if (Array.isArray(parsedConfig)) {
          // Merge saved config with default config to ensure new columns are included
          const mergedConfig = defaultConfig.map(defaultCol => {
            const savedCol = parsedConfig.find(saved => saved.id === defaultCol.id);
            return savedCol ? {
              ...defaultCol,
              visible: savedCol.visible,
              label: savedCol.label || defaultCol.label
            } : defaultCol;
          });
          
          return mergedConfig;
        }
      } catch (error) {
        console.error('Błąd podczas parsowania zapisanych kolumn:', error);
      }
    }
    
    // Return default configuration if no saved config or parsing failed
    return defaultConfig;
  };

  // Obsługa konfiguracji zakładek
  const handleTabConfigSave = (tabConfigs: TabConfig[]) => {
    // Save configuration to localStorage
    localStorage.setItem('tasks-tabs-config', JSON.stringify(tabConfigs));
    
    // Update tabs state
    setTabs(tabConfigs);
    
    // Update tab state based on configuration
    const defaultTab = tabConfigs.find(tab => tab.isDefault && tab.visible);
    if (defaultTab) {
      const visibleTabs = tabConfigs.filter(tab => tab.visible);
      const defaultTabIndex = visibleTabs.findIndex(tab => tab.id === defaultTab.id);
      if (defaultTabIndex !== -1) {
        setTabValue(defaultTabIndex);
        setCurrentView(defaultTab.view);
        setPage(0); // Reset page when switching tabs
      }
    }
    
    enqueueSnackbar('Konfiguracja zakładek została zapisana', { variant: 'success' });
  };

  const getTabConfigs = (): TabConfig[] => {
    const savedConfig = localStorage.getItem('tasks-tabs-config');
    if (savedConfig) {
      try {
        const parsedConfig: TabConfig[] = JSON.parse(savedConfig);
        if (Array.isArray(parsedConfig)) {
          return parsedConfig;
        }
      } catch (error) {
        console.error('Błąd podczas parsowania zapisanych zakładek:', error);
      }
    }
    
    // Return default configuration
    return [...defaultTabs];
  };

  // Funkcje obsługi paginacji
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Funkcja do obsługi kliknięcia nagłówka kolumny
  const handleSort = (key: keyof TaskDTO) => {
    // Map TaskDTO fields to backend sort fields
    const sortFieldMap: Record<keyof TaskDTO, 'ID' | 'TITLE' | 'STATUS' | 'PRIORITY' | 'TASK_TYPE' | 'ASSIGNED_TO' | 'DEADLINE' | 'CREATED_AT'> = {
      id: 'ID',
      title: 'TITLE',
      status: 'STATUS',
      priority: 'PRIORITY',
      taskType: 'TASK_TYPE',
      assignedToUserId: 'ASSIGNED_TO',
      endDate: 'DEADLINE',
      createdAt: 'CREATED_AT',
      // Default mappings for other fields
      description: 'TITLE',
      startDate: 'DEADLINE',
      updatedAt: 'CREATED_AT',
      deletedAt: 'CREATED_AT',
      createdByUserId: 'CREATED_AT',
      prefixId: 'ID',
      leads: 'ID', // Default mapping for leads field
    };

    const backendSortField = sortFieldMap[key];
    if (!backendSortField) return;

    // Toggle direction if same field, otherwise start with ASC
    const newDirection: 'ASC' | 'DESC' = 
      sortBy === backendSortField && sortDirection === 'ASC' ? 'DESC' : 'ASC';

    setSortBy(backendSortField);
    setSortDirection(newDirection);
    setPage(0); // Reset page when sorting changes
  };


  return (
    <Box sx={{ width: '100%' }}>
      {/* Menu wyboru priorytetu */}
      <Menu
        anchorEl={priorityMenuAnchor.element}
        open={Boolean(priorityMenuAnchor.element)}
        onClose={handleClosePriorityMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {TASK_PRIORITY_OPTIONS.map((option) => {
          const isCurrentPriority = tasks.find(t => t.id === priorityMenuAnchor.taskId)?.priority === option.value;
          return (
            <MenuItem 
              key={option.value} 
              onClick={() => handlePriorityChange(option.value as 'A' | 'B' | 'C' | 'D' | 'E')}
              sx={{
                backgroundColor: isCurrentPriority ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: getPriorityColor(option.value as 'A' | 'B' | 'C' | 'D' | 'E'),
                  borderRadius: '50%'
                }}
              />
              <Typography>{option.label}</Typography>
              {isCurrentPriority && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Menu wyboru użytkownika */}
      <UserSearchMenu
        anchorEl={assignUserMenuAnchor.element}
        open={Boolean(assignUserMenuAnchor.element)}
        onClose={() => setAssignUserMenuAnchor({ element: null, taskId: 0 })}
        onSelectUser={(userId) => {
          if (userId === undefined) {
            handleUnassignUser();
          } else {
            handleAssignUser(userId);
          }
        }}
        users={domainUsers}
        isLoading={loadingUsers}
        currentUserId={tasks.find(t => t.id === assignUserMenuAnchor.taskId)?.assignedToUserId}
        getUserDisplayName={getUserFullName}
      />


      {/* Menu wyboru statusu */}
      <Menu
        anchorEl={statusMenuAnchor.element}
        open={Boolean(statusMenuAnchor.element)}
        onClose={handleCloseStatusMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {TASK_STATUS_OPTIONS.map((option) => {
          const isCurrentStatus = tasks.find(t => t.id === statusMenuAnchor.taskId)?.status === option.value;
          return (
            <MenuItem 
              key={option.value} 
              onClick={() => handleStatusChange(option.value as 'TODO' | 'IN_PROGRESS' | 'CANCELED' | 'DONE')}
              sx={{
                backgroundColor: isCurrentStatus ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: getStatusColor(option.value),
                  borderRadius: '50%'
                }}
              />
              <Typography>{option.label}</Typography>
              {isCurrentStatus && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Menu wyboru typu zadania */}
      <Menu
        anchorEl={taskTypeMenuAnchor.element}
        open={Boolean(taskTypeMenuAnchor.element)}
        onClose={handleCloseTaskTypeMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {TASK_TYPE.map((option) => {
          const isCurrentTaskType = tasks.find(t => t.id === taskTypeMenuAnchor.taskId)?.taskType === option.value;
          return (
            <MenuItem 
              key={option.value} 
              onClick={() => handleTaskTypeChange(option.value as 'EMAIL' | 'PHONE' | 'MEETING' | 'MESSAGE' | 'OTHER')}
              sx={{
                backgroundColor: isCurrentTaskType ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: getTaskTypeColor(option.value),
                  borderRadius: '50%'
                }}
              />
              <Typography>{option.label}</Typography>
              {isCurrentTaskType && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Dialog potwierdzający usunięcie zadania */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Potwierdź usunięcie zadania
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Czy na pewno chcesz usunąć zadanie "{taskToDelete?.title}"? Tej operacji nie można cofnąć.
            {taskFiles.length > 0 && (
              <>
                <br /><br />
                <strong>Uwaga:</strong> To zadanie ma {taskFiles.length} powiązanych plików, które po usunięciu zadania będą przeniesione do kosza.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Anuluj</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Usuń zadanie
          </Button>
        </DialogActions>
      </Dialog>

      {/* Belka z newTask, wyszukiwarką i select */}
      <TaskSearchBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
        taskTypeFilter={taskTypeFilter}
        setTaskTypeFilter={setTaskTypeFilter}
        userFilter={userFilter}
        setUserFilter={setUserFilter}
        clientFilter={clientFilter}
        setClientFilter={setClientFilter}
        startDateFromFilter={startDateFromFilter}
        setStartDateFromFilter={setStartDateFromFilter}
        startDateToFilter={startDateToFilter}
        setStartDateToFilter={setStartDateToFilter}
        createdAtFromFilter={createdAtFromFilter}
        setCreatedAtFromFilter={setCreatedAtFromFilter}
        createdAtToFilter={createdAtToFilter}
        setCreatedAtToFilter={setCreatedAtToFilter}
        setPage={setPage}
        refreshTasks={async () => { 
          // Dispatch global event to refresh all tabs
          window.dispatchEvent(new CustomEvent('refresh-tasks')); 
        }}
        getUserFullName={getUserFullName}
        domainUsers={domainUsers}
        loadingUsers={loadingUsers}
      />

      <Paper sx={{ 
          width: '100%',
          mb: 2,
          overflow: 'hidden',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          bgcolor: '#ffffff',
        }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="zadania zakładki"
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.filter(tab => tab.visible).map((tab, index) => {
              const count = getTaskCountForView(tab.view);
              
              // Get appropriate color based on tab view type
              const getChipColor = (view: string) => {
                switch (view) {
                  case 'TODO':
                    return '#2196f3'; // Blue
                  case 'IN_PROGRESS':
                    return '#ff9800'; // Orange
                  case 'COMPLETED':
                    return '#4caf50'; // Green
                  case 'OVERDUE':
                    return '#f44336'; // Red
                  case 'ASSIGNED_TO_ME':
                    return '#9e9e9e'; // Gray
                  case 'ALL':
                  default:
                    return '#2196f3'; // Default blue
                }
              };
              
              return (
                <Tab
                  key={tab.id}
                  label={
                    <Box sx={{ fontSize:'0.9em', display: 'flex', alignItems: 'center' }}>
                      {tab.label}
                      {count > 0 && (
                        <Chip
                          size="small"
                          label={count}
                          sx={{ 
                            ml: 1, 
                            pointerEvents: 'none',
                            bgcolor: getChipColor(tab.view),
                            color: 'white'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </Box>
                  }
                  {...a11yProps(index)}
                />
              );
            })}
          </Tabs>
        </Box>
      </Paper>

      <Paper sx={{
        width: '100%',
        overflow: 'hidden',
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        bgcolor: '#ffffff',
        }}>
        <TabPanel value={tabValue} index={tabValue}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : tasks.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
              <Typography>Brak zadań do wyświetlenia</Typography>
              {(searchTerm || priorityFilter !== '') && (
                <Button 
                  variant="text" 
                  size="small"
                  onClick={() => {
                    setSearchTerm('');
                    setPriorityFilter('');
                    setPage(0); // Reset page when clearing filters
                  }}
                  sx={{ mt: 1 }}
                >
                  Wyczyść filtry
                </Button>
              )}
            </Box>
          ) : (
            <>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #e5e7eb',
                px: 2,
                py: 0
              }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SettingsIcon />}
                    onClick={() => setTabConfigOpen(true)}
                    sx={{
                      fontSize: '0.8rem',
                      minWidth: 'auto',
                      px: 2,
                      borderRadius: '9999px'
                    }}
                  >
                    Zakładki
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SettingsIcon />}
                    onClick={() => setColumnConfigOpen(true)}
                    sx={{
                      fontSize: '0.8rem',
                      minWidth: 'auto',
                      px: 2,
                      borderRadius: '9999px'
                    }}
                  >
                    Kolumny
                  </Button>
                </Box>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={totalElements}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Wierszy na stronę:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} z ${count}`}
                  sx={{
                    '.MuiTablePagination-toolbar': {
                      backgroundColor: 'transparent',
                      fontSize: '0.8rem',
                      border: 'none',
                    },
                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                      fontSize: '0.8rem',
                    },
                  }}
                />
              </Box>
              <TableContainer sx={{ width: '100%' }}>
              <Table 
                stickyHeader
                sx={{
                  '& td, & th': {
                    fontSize: '0.8rem',
                    paddingY: '4px',
                    paddingX: '8px',
                  },
                }}
                aria-label="tabela zadań"
              >
                <TableHead>
                  <TableRow sx={{ height: 48 }}>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id.toString()}
                        onClick={() => column.id !== 'actions' && handleSort(column.id as keyof TaskDTO)}
                        align={column.align}
                        sx={{
                          minWidth: column.minWidth,
                          backgroundColor: '#ffffff',
                          color: '#1f2937',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          borderBottom: '1px solid #e5e7eb',
                          cursor: column.id !== 'actions' ? 'pointer' : 'default',
                          userSelect: 'none'
                        }}
                      >
                        {column.label}
                        {column.id !== 'actions' && (() => {
                          const sortFieldMap: Record<string, string> = {
                            'id': 'ID',
                            'title': 'TITLE',
                            'status': 'STATUS',
                            'priority': 'PRIORITY',
                            'taskType': 'TASK_TYPE',
                            'assignedToUserId': 'ASSIGNED_TO',
                            'leads': 'ID',
                            'endDate': 'DEADLINE',
                            'createdAt': 'CREATED_AT',
                            'startDate': 'DEADLINE'
                          };
                          const currentSortField = sortFieldMap[column.id as string];
                          return currentSortField === sortBy && (
                            <Box component="span" ml={1}>
                              {sortDirection === 'ASC' ? '↑' : '↓'}
                            </Box>
                          );
                        })()}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.map((task, index) => {
                    return (
                      <TableRow
                        key={task.id}
                        hover
                        onClick={() => task.id && handleOpenDetailDialog(task.id)}
                        sx={{
                          backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: '#e0f2fe',
                          },
                          height: '36px',
                          '& td': {
                            py: '4px',
                          },
                        }}
                      >
                        {columns.map((column) => {
                          // Renderowanie komórki w zależności od typu kolumny
                          if (column.id === 'id') {
                            return (
                              <TableCell key={column.id}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.8rem' }}>
                                  {task.prefixId && task.id ? `${task.prefixId}-${task.id}` : task.id}
                                </Typography>
                              </TableCell>
                            );
                          }
                          if (column.id === 'title') {
                            return (
                              <TableCell key={column.id} component="th" scope="row">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    {task.title}
                                  </Typography>
                                </Box>
                              </TableCell>
                            );
                          }
                          if (column.id === 'status') {
                            return (
                              <TableCell key={column.id}>
                                <Chip
                                  size="small"
                                  label={getStatusLabel(task.status)}
                                  sx={{
                                    backgroundColor: getStatusColor(task.status),
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem'
                                  }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    const anchorEl = event.currentTarget;
                                    setStatusMenuAnchor({ element: anchorEl, taskId: task.id || 0 });
                                  }}
                                />
                              </TableCell>
                            );
                          }
                          if (column.id === 'priority') {
                            return (
                              <TableCell key={column.id}>
                                <Chip
                                  size="small"
                                  label={getPriorityLabel(task.priority)}
                                  sx={{
                                    backgroundColor: getPriorityColor(task.priority),
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem'
                                  }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    const anchorEl = event.currentTarget;
                                    setPriorityMenuAnchor({ element: anchorEl, taskId: task.id || 0 });
                                  }}
                                />
                              </TableCell>
                            );
                          }
                          if (column.id === 'taskType') {
                            return (
                              <TableCell key={column.id}>
                                <Chip
                                  size="small"
                                  label={getTaskTypeLabel(task.taskType)}
                                  sx={{
                                    backgroundColor: getTaskTypeColor(task.taskType),
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem'
                                  }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    const anchorEl = event.currentTarget;
                                    setTaskTypeMenuAnchor({ element: anchorEl, taskId: task.id || 0 });
                                  }}
                                />
                              </TableCell>
                            );
                          }
                          if (column.id === 'assignedToUserId') {
                            return (
                              <TableCell key={column.id}>
                                <Tooltip title={getUserFullName(task.assignedToUserId)}>
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      bgcolor: getUserColor(task.assignedToUserId),
                                      cursor: 'pointer',
                                      fontSize: '0.8rem',
                                    }}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      const anchorEl = event.currentTarget;
                                      setAssignUserMenuAnchor({ element: anchorEl, taskId: task.id || 0 });
                                    }}
                                  >
                                    {task.assignedToUserId ? getUserInitials(task.assignedToUserId) : <PersonIcon fontSize="small" />}
                                  </Avatar>
                                </Tooltip>
                              </TableCell>
                            );
                          }
                          if (column.id === 'leads') {
                            return (
                              <TableCell key={column.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {!task.leads || task.leads.length === 0 ? (
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                      Brak kontaktów
                                    </Typography>
                                  ) : (
                                    <>
                                      {/* First contact as plain text */}
                                      <Box
                                        sx={{ 
                                          display: 'flex', 
                                          flexDirection: 'column', 
                                          alignItems: 'flex-start',
                                          cursor: 'pointer',
                                          '&:hover': { color: 'primary.main' }
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (task.leads && task.leads[0]?.id) {
                                            setSelectedLeadId(task.leads[0].id);
                                            setOpenLeadDetailDialog(true);
                                          }
                                        }}
                                      >
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.2, fontWeight: 500 }}>
                                          {task.leads[0].firstName} {task.leads[0].lastName}
                                        </Typography>
                                        {task.leads[0].email && task.leads[0].phone ? (
                                          // Both email and phone
                                          <>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.1 }}>
                                              {task.leads[0].email}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.1 }}>
                                              {task.leads[0].phone}
                                            </Typography>
                                          </>
                                        ) : task.leads[0].email ? (
                                          // Only email
                                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.1 }}>
                                            {task.leads[0].email}
                                          </Typography>
                                        ) : task.leads[0].phone ? (
                                          // Only phone
                                          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.1 }}>
                                            {task.leads[0].phone}
                                          </Typography>
                                        ) : null}
                                      </Box>
                                      
                                      {/* Additional contacts indicator */}
                                      {task.leads.length > 1 && (
                                        <Tooltip
                                          title={
                                            <Box>
                                              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                Pozostałe kontakty:
                                              </Typography>
                                              {task.leads.slice(1).map((lead, index) => (
                                                <Box key={index} sx={{ mb: 0.5 }}>
                                                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                    {lead.firstName} {lead.lastName}
                                                  </Typography>
                                                  {lead.email && (
                                                    <Typography variant="body2" sx={{ fontSize: '0.65rem', color: 'white' }}>
                                                      {lead.email}
                                                    </Typography>
                                                  )}
                                                  {lead.phone && (
                                                    <Typography variant="body2" sx={{ fontSize: '0.65rem', color: 'white' }}>
                                                      {lead.phone}
                                                    </Typography>
                                                  )}
                                                </Box>
                                              ))}
                                            </Box>
                                          }
                                          arrow
                                          placement="top"
                                        >
                                          <Box
                                            sx={{
                                              width: 20,
                                              height: 20,
                                              borderRadius: '50%',
                                              backgroundColor: 'primary.main',
                                              color: 'white',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              cursor: 'pointer',
                                              fontSize: '0.7rem',
                                              fontWeight: 'bold'
                                            }}
                                          >
                                            +{task.leads.length - 1}
                                          </Box>
                                        </Tooltip>
                                      )}
                                    </>
                                  )}
                                </Box>
                              </TableCell>
                            );
                          }
                          if (column.id === 'startDate') {
                            return (
                              <TableCell key={column.id}>
                                <Box sx={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center' }}>
                                  {task.startDate ? format(new Date(task.startDate), 'dd MMM yyyy HH:mm', { locale: pl }) : 'Brak terminu od'}
                                </Box>
                              </TableCell>
                            );
                          }
                          if (column.id === 'endDate') {
                            return (
                              <TableCell key={column.id}>
                                <Box sx={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center' }}>
                                  {task.endDate ? format(new Date(task.endDate), 'dd MMM yyyy HH:mm', { locale: pl }) : 'Brak terminu do'}
                                </Box>
                              </TableCell>
                            );
                          }
                          if (column.id === 'createdAt') {
                            return (
                              <TableCell key={column.id}>
                                <Box sx={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center' }}>
                                  {task.createdAt ? format(new Date(task.createdAt), 'dd MMM yyyy HH:mm', { locale: pl }) : 'Brak daty'}
                                </Box>
                              </TableCell>
                            );
                          }
                          if (column.id === 'actions') {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <IconButton
                                  aria-label="edytuj"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    task.id && handleOpenDetailDialog(task.id);
                                  }}
                                  color="primary"
                                  size="small"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  aria-label="komentarze"
                                  onClick={(e) => task.id && handleOpenCommentsDialog(e, task.id)}
                                  color="info"
                                  size="small"
                                >
                                  <CommentIcon fontSize="small" />
                                </IconButton>
                                {isAdmin && (
                                  <IconButton
                                    aria-label="usuń"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteClick(task);
                                    }}
                                    color="error"
                                    size="small"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                            );
                          }
                          return null;
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Wierszy na stronę:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} z ${count}`}
              sx={{
                '.MuiTablePagination-toolbar': {
                  backgroundColor: '#f9fafb',
                  fontSize: '0.8rem',
                  borderTop: '1px solid #e5e7eb',
                },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontSize: '0.8rem',
                },
              }}
            />
            </>
          )}
        </TabPanel>
      </Paper>

      {editTask && (
        <TaskDialog
          open={!!editTask}
          onClose={handleCloseEditDialog}
          task={editTask}
          onSave={handleSaveTask}
        />
      )}

      {/* Dialog szczegółów zadania */}
      <TaskDetailDialog
        open={detailDialogOpen}
        taskId={selectedTaskId}
        onClose={handleCloseDetailDialog}
        onTaskUpdated={async () => {
          // Dispatch global event to refresh all tabs
          window.dispatchEvent(new CustomEvent('refresh-tasks'));
        }}
        initialTab={selectedTabIndex}
      />


      {/* Dialog konfiguracji kolumn */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onClose={() => setColumnConfigOpen(false)}
        columns={getColumnConfigs()}
        onSave={handleColumnConfigSave}
        title="Konfiguracja kolumn zadań"
      />

      {/* Dialog konfiguracji zakładek */}
      <TabConfigDialog
        open={tabConfigOpen}
        onClose={() => setTabConfigOpen(false)}
        tabs={getTabConfigs()}
        onSave={handleTabConfigSave}
        title="Konfiguracja zakładek zadań"
      />

      {/* Dialog szczegółów kontaktu */}
      <LeadDetailDialog
        open={openLeadDetailDialog}
        leadId={selectedLeadId}
        onClose={() => {
          setOpenLeadDetailDialog(false);
          setSelectedLeadId(null);
        }}
      />
    </Box>
  );
};

export default TasksPage;
